/**
 * Analytics Module
 * 
 * Handles analytics tracking for mockup processing events.
 * Provides a clean API for tracking user interactions and processing events.
 * 
 * Usage:
 *   AnalyticsModule.create({
 *     templateName: 'template-name',
 *     productTitle: 'Product Name',
 *     getProductSku: () => 'SKU-123',
 *     getAccessStatus: () => true
 *   });
 * 
 *   AnalyticsModule.start('image/jpeg', { template, product, productSku });
 *   AnalyticsModule.startMockup();
 *   AnalyticsModule.endMockup(previewDataUrl);
 */

(() => {
  'use strict';

  // Internal state
  let state = {
    logging: true,
    currentId: null,
    oldCxId: null,
    customerId: null,
    currentSessionId: null
  };

  let timeoutIds = {
    id1: null,
    id2: null
  };

  let getProductSku = () => '';

  // ============================================================================
  // Internal Helpers
  // ============================================================================

  const setCustomerId = (customerId) => {
    state.customerId = customerId;
    try {
      localStorage.setItem(AnalyticsModule.STORAGE_KEYS.CUSTOMER, customerId);
    } catch (error) {
      console.warn('[Analytics] Failed to save customer ID:', error);
    }
  };

  const setSessionId = (sessionId) => {
    state.currentSessionId = sessionId;
    try {
      sessionStorage.setItem(AnalyticsModule.STORAGE_KEYS.SESSION, sessionId);
    } catch (error) {
      console.warn('[Analytics] Failed to save session ID:', error);
    }
  };

  const generateUniqueId = () => {
    const timestamp = Math.floor(Date.now() / 1000);
    const randomNum = Math.floor(Math.random() * 1e5);
    const uniqueId = `${timestamp}${randomNum}`;
    return parseInt(uniqueId, 10);
  };

  const dataURLtoBlob = (dataURI, type = 'image/png') => {
    const byteString = atob(dataURI.split(',')[1]);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], {
      type
    });
  };

  const objectToFormData = (obj) => {
    const formData = new FormData();
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        if (Array.isArray(obj[key])) {
          obj[key].forEach((file) => {
            if (file instanceof File || file instanceof Blob) {
              formData.append(key, file);
            }
          });
        } else if (obj[key] instanceof File || obj[key] instanceof Blob) {
          formData.append(key, obj[key]);
        } else {
          formData.append(key, obj[key]);
        }
      }
    }
    return formData;
  };

  const sendRequest = (endpoint, data) => {
    if (!state.logging) return Promise.resolve();

    const formData = objectToFormData(data);
    const options = {
      method: 'POST',
      body: formData
    };

    return fetch(`${AnalyticsModule.API_URL}${endpoint}`, options)
      .then(response => {
        if (!response.ok) {
          return response.json().then(err => {
            throw new Error(`HTTP error! Status: ${response.status}, Message: ${err.message}`);
          });
        }
        return response.json();
      })
      .catch(error => {
        console.warn('[Analytics] Request error:', error);
      });
  };

  const setCurrentId = (id) => {
    state.currentId = id;
    try {
      localStorage.setItem(AnalyticsModule.STORAGE_KEYS.EVENT, id);
    } catch (error) {
      console.warn('[Analytics] Failed to save event ID:', error);
    }
    
    // Find and set the value in the input field
    const analyticsInput = document.querySelector('input[data-analytics-id-input]');
    if (analyticsInput) {
      analyticsInput.value = id;
    }
  };

  const clearCurrentId = () => {
    state.currentId = null;
    try {
      localStorage.removeItem(AnalyticsModule.STORAGE_KEYS.EVENT);
    } catch (error) {
      console.warn('[Analytics] Failed to clear event ID:', error);
    }
  };

  const mapStatus = (status) => {
    const statusMapping = {
      ok: 'OK',
      OK: 'OK',
      network: 'ERR_NETWORK',
      ERR_NETWORK: 'ERR_NETWORK',
      ERR_REMOVEBG: 'ERR_REMOVEBG',
      removebg: 'ERR_REMOVEBG',
      ERR_MOCKUP: 'ERR_MOCKUP',
      mockup: 'ERR_MOCKUP',
      UNKNOWN: 'UNKNOWN'
    };
    return statusMapping[status] || 'UNKNOWN';
  };

  const clearAllTimeouts = () => {
    if (timeoutIds.id1) {
      clearTimeout(timeoutIds.id1);
      timeoutIds.id1 = null;
    }
    if (timeoutIds.id2) {
      clearTimeout(timeoutIds.id2);
      timeoutIds.id2 = null;
    }
  };

  const logTime10sEvent = () => {
    if (!state.currentId) {
      console.warn('[Analytics] No active event to log time.');
      return;
    }
    const data = {
      tempEventId: state.currentId,
      eventType: 'TIME_10S'
    };
    sendRequest('/events', data).catch(error => {
      console.warn('[Analytics] Failed to log 10s event:', error);
    });
  };

  const logTime20sEvent = () => {
    if (!state.currentId) {
      console.warn('[Analytics] No active event to log time.');
      return;
    }
    const data = {
      tempEventId: state.currentId,
      eventType: 'TIME_20S'
    };
    sendRequest('/events', data).catch(error => {
      console.warn('[Analytics] Failed to log 20s event:', error);
    });
  };

  const start10sTimeout = () => {
    clearAllTimeouts();
    timeoutIds.id1 = setTimeout(() => {
      logTime10sEvent();
    }, 10000);
    timeoutIds.id2 = setTimeout(() => {
      logTime20sEvent();
    }, 20000);
  };

  const createSession = () => {
    const data = {
      tempCxId: state.oldCxId,
      customerId: state.customerId
    };

    sendRequest('/sessions', data)
      .then(resp => {
        const {
          customerId,
          sessionId
        } = resp;
        setCustomerId(customerId);
        setSessionId(sessionId);
      })
      .catch(error => {
        console.warn('[Analytics] Failed to create session:', error);
      });
  };

  const initSession = () => {
    try {
      const storedSessionId = sessionStorage.getItem(AnalyticsModule.STORAGE_KEYS.SESSION);
      if (storedSessionId) {
        state.currentSessionId = storedSessionId;
      } else {
        createSession();
      }
    } catch (error) {
      console.warn('[Analytics] Failed to init session:', error);
    }
  };

  // ============================================================================
  // Public API
  // ============================================================================

  const AnalyticsModule = {
    // API URL for analytics endpoint
    API_URL: 'https://aav-services.com/api/shop/mockup',

    // Storage keys
    STORAGE_KEYS: {
      SESSION: '__mSsId',
      CUSTOMER: '__mCmId',
      OLD_CUSTOMER: 'mAnalyticsCxId',
      EVENT: '__mEventId'
    },

    /**
     * Initialize analytics module
     * @param {Object} config - Configuration object
     * @param {string} config.templateName - Template name
     * @param {string} config.productTitle - Product title
     * @param {Function} config.getProductSku - Function that returns current product SKU
     * @param {Function} config.getAccessStatus - Function that returns access status (default: always true)
     */
    create: (config = {}) => {
      // Store config functions
      getProductSku = config.getProductSku || (() => '');

      // Initialize state
      state = {
        logging: true,
        currentId: null,
        oldCxId: localStorage.getItem(AnalyticsModule.STORAGE_KEYS.OLD_CUSTOMER) || null,
        customerId: localStorage.getItem(AnalyticsModule.STORAGE_KEYS.CUSTOMER) || null,
        currentSessionId: null
      };

      timeoutIds = {
        id1: null,
        id2: null
      };

      // Initialize session
      initSession();
    },

    /**
     * Start a new analytics event
     * @param {Object} options - Options object
     * @param {string} options.imageType - Image MIME type (e.g., 'image/jpeg')
     * @param {string} options.template - Template name
     * @param {string} options.product - Product title
     * @param {string} options.productSku - Product SKU
     * @param {string} options.processType - Process type
     */
    start: (options = {}) => {
      try {
        clearCurrentId();
        setCurrentId(generateUniqueId());
        const {
          imageType = null,
          processType = 'unknown',
          product = null,
          productSku = null,
          template = null,
          generationType = null,
        } = options;
        
        const processStart = Date.now();
        const data = {
          tempEventId: state.currentId,
          customerId: state.customerId,
          processStart,
          imageType: imageType,
          sessionId: state.currentSessionId,
          product: product,
          template: template,
          productSku: productSku,
          urlPath: window.location.pathname,
          processType: processType,
          generationType: generationType,
          eventType: 'START'
        };

        sendRequest('/events', data);
        start10sTimeout();
      } catch (error) {
        console.warn('[Analytics] Failed to start event:', error);
      }
    },

    /**
     * Start mockup generation
     */
    startMockup: () => {
      try {
        if (!state.currentId) {
          console.warn('[Analytics] No active event to start mockup.');
          return;
        }

        const mockupStart = Date.now();
        const data = {
          tempEventId: state.currentId,
          mockupStart,
          eventType: 'START_MOCKUP'
        };

        setTimeout(() => {
          sendRequest('/events', data);
        }, 300);
      } catch (error) {
        console.warn('[Analytics] Failed to start mockup:', error);
      }
    },

    /**
     * End mockup generation
     * @param {string|boolean} mockupDataUrl - Data URL of mockup preview (optional)
     */
    endMockup: (mockupDataUrl = false, colorEvent = false) => {
      try {
        if (!state.currentId) {
          console.warn('[Analytics] No active event to end mockup.');
          return;
        }

        const mockupBlob = mockupDataUrl ? dataURLtoBlob(mockupDataUrl) : null;
        const mockupEnd = Date.now();

        const data = {
          tempEventId: state.currentId,
          mockupEnd,
          colorChange: colorEvent,
          eventType: 'END_MOCKUP',
          mockupFile: mockupBlob
        };

        sendRequest('/events', data);
      } catch (error) {
        console.warn('[Analytics] Failed to end mockup:', error);
      }
    },

    /**
     * Start save operation
     */
    startSave: () => {
      try {
        if (!state.currentId) {
          console.warn('[Analytics] No active event to start save.');
          return;
        }

        const saveStart = Date.now();
        const data = {
          tempEventId: state.currentId,
          saveStart,
          eventType: 'START_SAVE'
        };

        setTimeout(() => {
          sendRequest('/events', data);
        }, 1000);
      } catch (error) {
        console.warn('[Analytics] Failed to start save:', error);
      }
    },

    /**
     * End save operation
     * @param {string|null} saveUrl - URL of saved file (optional)
     */
    endSave: (saveUrl = null) => {
      try {
        if (!state.currentId) {
          console.warn('[Analytics] No active event to end save.');
          return;
        }

        const saveEnd = Date.now();
        const data = {
          tempEventId: state.currentId,
          saveEnd,
          saveUrl,
          eventType: 'END_SAVE'
        };

        sendRequest('/events', data);
      } catch (error) {
        console.warn('[Analytics] Failed to end save:', error);
      }
    },

    /**
     * End process
     * @param {string} endStatus - Process end status
     * @param {Object} options - Optional data
     * @param {string} options.fileUrl - File URL
     * @param {Object} options.error - Error object
     */
    endProcess: (endStatus, options = {}) => {
      try {
        if (!state.currentId) {
          console.warn('[Analytics] No active event to end process.');
          return;
        }

        const {
          fileUrl = null, error = false, skipMockup = false, skipSave = false
        } = options;
        const status = mapStatus(endStatus);
        const processEnd = Date.now();

        let data = {
          tempEventId: state.currentId,
          processEnd,
          status,
          fileUrl,
          skipMockup,
          skipSave,
          eventType: 'END_PROCESS'
        };

        if (error) {
          if (endStatus === 'ERR_MOCKUP' && error.message &&
            !error.message.includes('Request failed with status') &&
            !error.name?.includes('AxiosError')) {
            try {
              const errorDetails = `Error Name: ${error.name}\nError Stack: ${error.stack}`;
              data.errorMessageDetailed = JSON.stringify(errorDetails);
            } catch (e) {
              console.warn('[Analytics] Failed to stringify error details:', e);
            }
          }
          try {
            data.errorMessage = JSON.stringify(error.message);
          } catch (e) {
            console.warn('[Analytics] Failed to stringify error message:', e);
          }
        }

        clearAllTimeouts();
        sendRequest('/events', data);
      } catch (err) {
        console.warn('[Analytics] Failed to end process:', err);
      }
    },

    /**
     * Set logging enabled/disabled
     * @param {boolean} value - Enable or disable logging
     */
    setLogging: (value) => {
      if (typeof value !== 'boolean') {
        console.warn('[Analytics] Invalid value for logging. Expected a boolean.');
        return;
      }
      state.logging = value;
    },

    /**
     * Track add to cart event
     * @param {string} productSku - Product SKU
     */
    toCart: (productSku = '') => {
      try {
        // Get current event ID from localStorage
        const currentId = localStorage.getItem(AnalyticsModule.STORAGE_KEYS.EVENT);
        
        if (!currentId) {
          console.log('[Analytics] No active event to track add to cart.');
          return;
        }

        const data = {
          tempEventId: currentId,
          productSku: productSku,
          eventType: 'TO_CART'
        };

        sendRequest('/events', data);
      } catch (error) {
        console.warn('[Analytics] Failed to send toCart event:', error);
      }
    }
  };

  // Export to window
  if (typeof window !== 'undefined') {
    window.AnalyticsModule = AnalyticsModule;
  }
})();